import type { AssignmentStatus, HomeworkAssignmentSnapshot } from '@wx-weiqi/contracts';

import { nextProblemIndex } from './homework-answer-progress';
import { latestHomeworkSubmissionForProblem } from './homework-grading';
import { STUDENT_HOMEWORK_STATUSES } from './homework-list-presentation';

export type StudentHomeLearningSummary =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly message: string }
  | { readonly status: 'empty' }
  | {
      readonly status: 'ready';
      readonly assignment: {
        readonly assignmentId: string;
        readonly title: string;
        readonly submittedCount: number;
        readonly totalCount: number;
        readonly status: AssignmentStatus;
        readonly statusLabel: string;
        readonly canContinue: boolean;
      };
    };

/** A navigation hint only: the server still authorizes each answer and its deadline. */
function canContinueAssignment(
  assignment: HomeworkAssignmentSnapshot,
  serverNowMs: number,
): boolean {
  return (
    (assignment.status === 'ASSIGNED' || assignment.status === 'IN_PROGRESS') &&
    (assignment.dueAt === null || serverNowMs <= Date.parse(assignment.dueAt)) &&
    nextProblemIndex(assignment) !== null
  );
}

/** Selects within the loaded page; it does not imply a total count of outstanding work. */
export function studentHomeLearningSummary(
  assignments: readonly HomeworkAssignmentSnapshot[],
  serverTimestamp: string,
): StudentHomeLearningSummary {
  const serverNowMs = Date.parse(serverTimestamp);
  const newestFirst = [...assignments].sort(
    (left, right) =>
      Date.parse(right.assignedAt) - Date.parse(left.assignedAt) ||
      right.assignmentId.localeCompare(left.assignmentId),
  );
  const assignment =
    newestFirst.find(
      (candidate) =>
        candidate.status === 'IN_PROGRESS' && canContinueAssignment(candidate, serverNowMs),
    ) ??
    newestFirst.find((candidate) => canContinueAssignment(candidate, serverNowMs)) ??
    newestFirst[0];
  if (assignment === undefined) return { status: 'empty' };
  return {
    status: 'ready',
    assignment: {
      assignmentId: assignment.assignmentId,
      title: assignment.title,
      submittedCount: assignment.items.filter(
        (item) => latestHomeworkSubmissionForProblem(assignment, item.problem.problemId) !== null,
      ).length,
      totalCount: assignment.items.length,
      status: assignment.status,
      statusLabel: STUDENT_HOMEWORK_STATUSES[assignment.status].label,
      canContinue: canContinueAssignment(assignment, serverNowMs),
    },
  };
}
